import {Component, DestroyRef, EventEmitter, Inject, inject, Input, LOCALE_ID, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {DatePipe} from '@angular/common';
import {IWerdegang} from '../../../shared/model/werdegang.model';
import {Berater} from '../../../shared/model/berater.model';
import {BeraterService} from '../../../services/berater.service';
import {MatButton, MatIconButton, MatMiniFabButton} from '@angular/material/button';
import {MatError, MatFormField, MatHint, MatInput, MatLabel, MatSuffix} from '@angular/material/input';
import {
  MatDatepicker,
  MatDatepickerActions,
  MatDatepickerApply,
  MatDatepickerCancel,
  MatDatepickerInput,
  MatDatepickerToggle
} from '@angular/material/datepicker';
import {TranslatePipe} from '@ngx-translate/core';
import {IFaehigkeit} from '../../../shared/model/faehigkeit.model';
import {MatIcon} from '@angular/material/icon';
import {MatCard, MatCardContent} from '@angular/material/card';
import {MessageService} from '../../../shared/components/message.service';
import {DateService} from '../../../services/date.service';
import {MatCheckbox} from '@angular/material/checkbox';
import {FieldHintComponent} from '../../../shared/components/field-hint/field-hint.component';
import {ModalComponent} from '../../../shared/components/modal/modal.component';
import {ModalService} from '../../../shared/components/modal/modal.service';
import {InputUtils} from '../../../../utils/input-utils.component';
import {INPUT_LIMITS} from '../../../shared/constants/input-limits';
import {FieldWarningComponent} from '../../../shared/components/field-warning/field-warning.component';
import {ValidationFinding, findEntryFinding} from '../../../shared/model/validation-finding';

@Component({
  selector: 'app-career',
  standalone: true,
  imports: [
    DatePipe,
    ReactiveFormsModule,
    MatButton,
    MatInput,
    MatDatepickerInput,
    MatSuffix,
    MatDatepickerToggle,
    MatDatepicker,
    MatDatepickerActions,
    MatDatepickerCancel,
    MatDatepickerApply,
    MatFormField,
    MatError,
    MatLabel,
    TranslatePipe,
    MatIcon,
    MatMiniFabButton,
    MatCard,
    MatCardContent,
    MatCheckbox,
    ModalComponent,
    MatIconButton,
    MatHint,
    FieldHintComponent,
    FieldWarningComponent
  ],
  templateUrl: './career.component.html',
  styleUrl: './career.component.scss',
})
export class CareerComponent implements OnInit, OnChanges {
  @Input() werdegang: IWerdegang[] = [];
  @Input() exportMode: boolean;
  @Input() twoPagerMode: boolean = false;
  @Input() berater: Berater;
  @Input() forcedSelectedIds: string[] | null = null;
  @Input() findings: ValidationFinding[] = [];

  @Output() exportSelectionChange = new EventEmitter<string[]>();
  // NP20-828: siehe PersonalDataComponent — der Parent uebernimmt den gelieferten
  // Berater direkt, statt neu zu laden.
  @Output() saved = new EventEmitter<Berater>();

  private readonly destroyRef = inject(DestroyRef);

  selectedExportIds: string[] = [];
  @Inject(LOCALE_ID) public locale: string;

  editMode = false;
  /** NP20-822: Laeuft gerade ein Speichern, loest ein weiterer Klick keinen zweiten Request aus. */
  isSaving = false;
  form: FormGroup;
  stillWorking: boolean = false;
  careerCopy: IWerdegang[];
  originalCareer: IFaehigkeit[];
  formChanged = false;
  showModal = false;
  currentIdx: number | null = null;
  protected get LIMITS() { return INPUT_LIMITS; }

  constructor(
    private fb: FormBuilder,
    @Inject(LOCALE_ID) locale: string,
    private beraterService: BeraterService,
    private messageService: MessageService,
    private dateService: DateService,
    private modalService: ModalService
  ) {
    this.locale = locale;
  }

  get werdegangArray(): FormArray {
    return this.form.get('werdegang') as FormArray;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['werdegang'] && changes['werdegang'].currentValue && !changes['werdegang'].firstChange) {
      // Waehrend der Nutzer bearbeitet niemals das FormArray aus einem externen
      // Input-Update ueberschreiben (Reviewer-Hinweis PR #617).
      if (!this.editMode) {
        this.careerCopy = JSON.parse(JSON.stringify(changes['werdegang'].currentValue));
        this.werdegangArray.clear();
        if (this.werdegang?.length > 0) {
          this.werdegang.forEach(z => this.addCareerForm(z));
        }
      }
    }
    if (changes['exportMode'] || changes['twoPagerMode']) {
      if (this.exportMode && !this.twoPagerMode) {
        this.autoSelectAll();
      } else {
        this.selectedExportIds = [];
      }
    } else if (changes['werdegang'] && this.exportMode && !this.twoPagerMode) {
      this.autoSelectAll();
    }

    if (changes['forcedSelectedIds'] && this.forcedSelectedIds !== null) {
      this.selectedExportIds = [...this.forcedSelectedIds];
      this.exportSelectionChange.emit([...this.selectedExportIds]);
    }
  }

  private autoSelectAll(): void {
    this.selectedExportIds = (this.werdegang ?? []).map(w => w.uuid).filter((uuid): uuid is string => !!uuid);
    this.exportSelectionChange.emit([...this.selectedExportIds]);
  }

  toggleExportSelection(idx: string, checked: boolean): void {
    if (checked) {
      if (!this.selectedExportIds.includes(idx)) this.selectedExportIds.push(idx);
    } else {
      this.selectedExportIds = this.selectedExportIds.filter(i => i !== idx);
    }
    this.exportSelectionChange.emit([...this.selectedExportIds]);
  }

  ngOnInit() {
    this.form = this.fb.group({
      werdegang: this.fb.array([])
    });
    this.careerCopy = JSON.parse(JSON.stringify(this.werdegang ?? []));

    if (this.werdegang?.length > 0) {
      this.werdegang.forEach((z) => this.addCareerForm(z));
    }

    // takeUntilDestroyed statt "ngOnChanges-subscribe" — sonst leaked die
    // Subscription pro Input-Update (Reviewer-Hinweis PR #617).
    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.formChanged = this.hasChanges());
  }

  addCareerForm(data?: IWerdegang) {
    const group = this.fb.group({
      uuid: [data?.uuid || null],
      beginn: [data?.beginn || '', Validators.required],
      unternehmen: [data?.unternehmen || '', [Validators.required, Validators.maxLength(INPUT_LIMITS.SHORT_LABEL)]],
      stellenbezeichnung: [data?.stellenbezeichnung || '', [Validators.required, Validators.maxLength(INPUT_LIMITS.JOB_TITLE)]],
      ende: [data?.ende || ''],
      ort: [data?.ort || '', [Validators.required, Validators.maxLength(INPUT_LIMITS.LOCATION)]],
      taetigkeit: [data?.taetigkeit || '', [Validators.required, Validators.maxLength(INPUT_LIMITS.FREETEXT)]],
      stillWorking: [!data?.ende]
    });

    const updateEndeValidation = (isWorking: boolean) => {
      const endeControl = group.get('ende');
      if (isWorking) {
        endeControl!.clearValidators();
        endeControl!.setValue('');
      } else {
        endeControl!.setValidators([Validators.required]);
      }
      endeControl!.updateValueAndValidity();
    };

    group.get('stillWorking')!.valueChanges.subscribe(updateEndeValidation);
    updateEndeValidation(group.get('stillWorking')!.value);

    this.werdegangArray.push(group);
  }

  // NP20-916: Loeschen nur vormerken — Speichern schickt es mit, Abbrechen nimmt es zurueck.
  // Vorher lief sofort ein PUT und der Abbrechen-Knopf wurde gesperrt.
  delete(index: number) {
    this.werdegangArray.removeAt(index);
    this.formChanged = this.hasChanges();
    this.messageService.info('career.infoDeleteMessage');
  }

  save() {
    if (this.form.invalid || this.isSaving) return;

    this.werdegang = this.werdegangArray.value;

    this.werdegang = this.werdegang.map((item) => ({
      ...item,
      beginn: this.dateService.toLocalDateString(item.beginn),
      ende: this.dateService.toLocalDateString(item.ende)
    }));
    // NP20-915: editMode erst nach der Antwort schliessen, sonst sieht ein Fehlschlag wie Erfolg aus.
    this.isSaving = true;
    this.beraterService
      .updateWerdegang(this.werdegang, this.berater.id)
      .subscribe({
        next: (value) => {
          this.isSaving = false;
          this.editMode = false;
          this.berater = value;
          this.werdegang = this.berater.werdegang;
          this.careerCopy = JSON.parse(JSON.stringify(this.werdegang));
          this.formChanged = false;
          this.messageService.success('career.successMessage');
          this.saved.emit(value);
        },
        error: () => {
          this.isSaving = false;
          // Bearbeitungsmodus bleibt offen, die Eingaben stehen weiter im Formular.
          this.messageService.error('career.errorMessage');
        },
      });
  }

  onToggleEdit(): void {
    if (this.editMode) {
      this.save();
    } else {
      if (this.werdegang.length === 0) {
        this.addCareerForm();
      } else {
        this.werdegangArray.clear();
        this.werdegang.forEach((z) => this.addCareerForm(z));
      }

      this.originalCareer = JSON.parse(JSON.stringify(this.werdegang));
      this.careerCopy = JSON.parse(JSON.stringify(this.form.value.werdegang));

      this.editMode = true;
      this.formChanged = false;
    }
  }

  onCancelEdit() {
    this.werdegangArray.clear();
    this.originalCareer.forEach((a) => this.addCareerForm(a));
    this.editMode = false;
    this.formChanged = false;
  }

  hasChanges(): boolean {
    return (
      JSON.stringify(this.form.value.werdegang) !==
      JSON.stringify(this.careerCopy)
    );
  }

  openDeleteModal(idx: number) {
    this.currentIdx = idx;
    this.modalService.open('deleteCareer');
  }

  onKeyPress(event: KeyboardEvent) {
    InputUtils.allowLettersOnly(event);
  }

  entryFinding(entryIndex: number, field: string): ValidationFinding | null {
    if (this.editMode || this.exportMode) return null;
    return findEntryFinding(this.findings, entryIndex, field);
  }
}
